import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';

export interface Tenant {
  id: string;
  ruc: string;
  businessName: string;
  tradeName: string | null;
  createdAt?: string;
}

export interface CompanyModule {
  key: string;
  name: string;
  category: string;
  isCore: boolean;
  dependsOn: string[];
  enabled: boolean;
}

export interface SubscriptionSummary {
  status: string;
  currency: string;
  planKey: string;
  planName: string;
  planPrice: number;
  includedModules: string[];
  extras: Array<{ key: string; name: string; price: number }>;
  total: number;
  mpPreapprovalId: string | null;
}

export interface Plan {
  key: string;
  name: string;
  priceMonthly: number;
}

export function usePlans() {
  return useQuery({
    queryKey: ['plans'],
    queryFn: async () => (await apiClient.get<Plan[]>('/admin/plans')).data,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export function useSetTenantPlan(tenantId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (planKey: string) =>
      apiClient.put(`/admin/tenants/${tenantId}/subscription/plan`, { planKey }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant-subscription', tenantId] });
      qc.invalidateQueries({ queryKey: ['tenant-modules', tenantId] });
      qc.invalidateQueries({ queryKey: ['metrics'] });
    },
    onError: (e: unknown) => toast.error(errMsg(e, 'No se pudo cambiar el plan')),
  });
}

// ---- Empresas ----
export function useTenants() {
  return useQuery({
    queryKey: ['tenants'],
    queryFn: async () => (await apiClient.get<Tenant[]>('/admin/tenants')).data,
    retry: false,
  });
}

export function useTenant(id: string | undefined) {
  return useQuery({
    queryKey: ['tenant', id],
    queryFn: async () => (await apiClient.get<Tenant>(`/admin/tenants/${id}`)).data,
    enabled: !!id,
    retry: false,
  });
}

export interface NewTenantInput {
  ruc: string;
  businessName: string;
  tradeName?: string;
  owner: { email: string; name: string; password: string };
}

export function useCreateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: NewTenantInput) =>
      (await apiClient.post('/admin/tenants', input)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenants'] });
      qc.invalidateQueries({ queryKey: ['metrics'] });
      toast.success('Empresa creada');
    },
    onError: (e: unknown) => toast.error(errMsg(e, 'No se pudo crear la empresa')),
  });
}

// ---- Modulos por empresa ----
export function useTenantModules(tenantId: string | undefined) {
  return useQuery({
    queryKey: ['tenant-modules', tenantId],
    queryFn: async () =>
      (await apiClient.get<CompanyModule[]>(`/admin/tenants/${tenantId}/modules`)).data,
    enabled: !!tenantId,
    retry: false,
  });
}

export function useSetTenantModule(tenantId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, enabled }: { key: string; enabled: boolean }) =>
      apiClient.put(`/admin/tenants/${tenantId}/modules/${key}`, { enabled }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant-modules', tenantId] });
      qc.invalidateQueries({ queryKey: ['tenant-subscription', tenantId] });
      qc.invalidateQueries({ queryKey: ['metrics'] });
    },
    onError: (e: unknown) => toast.error(errMsg(e, 'No se pudo cambiar el modulo')),
  });
}

// ---- Suscripcion / pagos ----
export function useTenantSubscription(tenantId: string | undefined) {
  return useQuery({
    queryKey: ['tenant-subscription', tenantId],
    queryFn: async () =>
      (await apiClient.get<SubscriptionSummary>(`/admin/tenants/${tenantId}/subscription`)).data,
    enabled: !!tenantId,
    retry: false,
  });
}

export function useSetSubscriptionStatus(tenantId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (status: string) =>
      apiClient.put(`/admin/tenants/${tenantId}/subscription`, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant-subscription', tenantId] });
      qc.invalidateQueries({ queryKey: ['metrics'] });
    },
    onError: (e: unknown) => toast.error(errMsg(e, 'No se pudo cambiar el estado')),
  });
}

export function useTenantCheckout(tenantId: string | undefined) {
  return useMutation({
    mutationFn: async () =>
      (await apiClient.post<{ initPoint: string }>(`/admin/tenants/${tenantId}/subscription/checkout`))
        .data,
    onError: (e: unknown) => toast.error(errMsg(e, 'No se pudo generar el link de pago')),
  });
}

function errMsg(e: unknown, fallback: string): string {
  return (
    (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? fallback
  );
}
